export type HousingForm =
  | 'dwelling_house'
  // Single umbrella option shown in the form dropdown — the backend
  // assesses BOTH attached + detached and recommends one.
  | 'dual_occupancy'
  // Combined run — one report that assesses a single dwelling AND a
  // duplex on the same lot. The verdict shows both side by side and
  // sections C / D / E carry a sub-block per form.
  | 'dwelling_house_and_dual_occupancy'
  // Power-user / API-only values; not exposed in the dropdown but
  // accepted on the wire for direct sub-form requests.
  | 'dual_occupancy_attached'
  | 'dual_occupancy_detached'
  | 'manor_house'
  | 'terrace'
  | 'multi_dwelling_housing'
  | 'residential_flat_building'

export const HOUSING_FORM_LABELS: Record<HousingForm, string> = {
  dwelling_house: 'Dwelling house',
  dual_occupancy: 'Dual occupancy',
  dwelling_house_and_dual_occupancy: 'Single dwelling vs Duplex — compare both',
  dual_occupancy_attached: 'Dual occupancy (attached)',
  dual_occupancy_detached: 'Dual occupancy (detached)',
  manor_house: 'Manor house (Codes SEPP Part 3B)',
  terrace: 'Terraces (Codes SEPP Part 3B)',
  multi_dwelling_housing: 'Multi-dwelling housing (Codes SEPP Part 3B)',
  residential_flat_building: 'Residential flat building (DA only — no CDC)',
}

// The set of housing forms the FORM offers in the dropdown. Excludes
// the explicit attached / detached sub-forms so the planner picks
// the umbrella choice and the platform decides.
export const DROPDOWN_HOUSING_FORMS: HousingForm[] = [
  'dwelling_house',
  'dual_occupancy',
  'dwelling_house_and_dual_occupancy',
  'manor_house',
  'terrace',
  'multi_dwelling_housing',
  'residential_flat_building',
]

export interface FeasibilityRequest {
  address: string
  // One reference ("A/396594") or several separated by ";" when the
  // sale bundles more than one title ("A/396594; 462/752023") — the
  // backend fetches each parcel and unions them into one site.
  lot_dp?: string | null
  // Dimension overrides — when supplied, the backend uses these wholesale
  // instead of the polygon-derived estimate and tags the row "Verified".
  site_area_m2?: number | null
  frontage_m?: number | null
  depth_m?: number | null
  housing_form: HousingForm
  intended_storeys?: number | null
  // True only when the user actually picked a Section 10.7 cert or
  // Contract of Sale file in this session. Gates the orchestrator's
  // cache lookup so a previously-uploaded cert for the same lot/DP
  // doesn't silently bleed into a fresh run.
  apply_uploaded_docs?: boolean
  // When true (default on the form), the analyst background task
  // emails the user a link to the report once findings analysis
  // is ready. Opted-out users wait on the page instead.
  email_when_ready?: boolean
  // Explicit override for who the notification goes to. When
  // blank, the backend falls back to the user's Supabase JWT
  // email. Useful for sending the report to a colleague /
  // consultant / property owner.
  notify_email?: string | null
  // Low and Mid-Rise Housing area status the planner read off the
  // Department's indicative map. "inside" switches on the Housing
  // SEPP DA standards for a duplex and waives the LEP minimum lot
  // for a Pattern Book duplex; the report records it as supplied by
  // the planner. null = not sure (the report says "verify").
  lmr_area?: 'inside' | 'outside' | null
  // A second public road the lot's parking can come from, read off
  // the deposited plan: a rear lane (3–7 m wide public road), a second
  // street (the lot runs through the block) or a corner lot's
  // secondary road. Unlocks the Codes SEPP 12 m width for a duplex
  // and the front-and-back layout / Torrens split.
  rear_access?: 'lane' | 'street' | 'corner' | null
}

export interface ValueT {
  value: unknown
  source: string | null
  citation: string | null
  source_text: string | null
  confidence: 'green' | 'amber' | 'red' | 'grey'
  layer: string | null
  layer_version: string | null
  override_applied: boolean
  notes: string[]
  // Provenance enrichment — surfaced by the report's Source panel so
  // every number can be audited back to its origin.
  source_url?: string | null
  parsed_at?: string | null
  // Machine-readable detail behind the value (e.g. acid sulfate soils
  // class coverage fractions).
  detail?: Record<string, unknown> | null
}

export interface CachedDocumentsMeta {
  cert: {
    issued_at: string | null
    parsed_at: string | null
    council_name: string | null
    cert_number: string | null
  } | null
  contract: {
    parsed_at: string | null
    vendor: string | null
    contract_date: string | null
  } | null
}

export interface AnalystEvidence {
  label: string
  citation: string | null
  source_text: string | null
}

export interface AnalystTopic {
  topic: string
  label: string
  status: string
  status_tone: 'ok' | 'info' | 'caveat' | 'blocker' | 'unknown'
  implication: string
  actions: string[]
  evidence: AnalystEvidence[]
  confidence: 'high' | 'medium' | 'low'
  confidence_reason: string
  // Flat structured fields lifted out of the prose (BAL category,
  // easement area, floor level, etc.). Optional / variable per
  // topic — see analyst.md for the canonical key registry.
  metrics?: Record<string, string | number | boolean | null>
}

/** One bullet in the executive summary. `section_ref` is the report
 *  section letter ("A".."K") carrying the detail — rendered as
 *  "→ Section D". */
export interface SummaryPoint {
  text: string
  section_ref: string | null
}

/** The "phone-call version" of the report — what's achievable, what
 *  isn't, what to do first. Written by the analyst in client-facing
 *  English; rendered above everything else. Optional on older cached
 *  analyst rows. */
export interface ExecutiveSummary {
  bottom_line: string
  achievable: SummaryPoint[]
  not_achievable: SummaryPoint[]
  recommended_path: string | null
  first_actions: string[]
}

export interface AnalystOutput {
  topics: AnalystTopic[]
  headline: string | null
  biggest_risk: string | null
  missing_information: string[]
  executive_summary?: ExecutiveSummary | null
}

// Outcomes at a glance — council DA vs CDC for each form assessed,
// built server-side from the same objects Sections C / D / E / J
// render. Opens the executive summary.
export interface PathwayOutcome {
  name: string
  kind: 'yes' | 'conditional' | 'no' | 'verify'
  status: string
  open: boolean | null
  max_gfa_m2: number | null
  basis: string | null
  approval_time: string | null
  approval_cost: string | null
  design_flexibility: string | null
  risk: string | null
}

export interface FormOutcome {
  housing_form: string
  label: string
  sublabel: string
  council: PathwayOutcome
  cdc: PathwayOutcome
  recommended: string | null
  rationale: string | null
  difference_m2: number | null
  difference_text: string | null
  dual_occupancy: {
    permitted: boolean | null
    binding_pathway: string | null
    binding_pathway_label: string | null
    torrens_subdivision: boolean | null
    recommended_subform: string | null
  } | null
}

export interface Outcomes {
  combined: boolean
  headline: string | null
  recommended_form_label: string | null
  forms: FormOutcome[]
}

export interface FeasibilityReport {
  id: string
  address: string
  disclaimer: string
  // The analyst's reconciliation of findings — plain-English per
  // topic. Soft-failed when null (model call died / no API key).
  analyst?: AnalystOutput | null
  // Council DA vs CDC per form — rendered inside the executive summary.
  outcomes?: Outcomes | null
  // Populated when the user didn't opt into cached documents but
  // previously-uploaded cert / contract data exists for this lot.
  // The Report renders an opt-in banner; clicking Apply re-runs with
  // apply_uploaded_docs=true.
  cached_documents?: CachedDocumentsMeta | null
  sections: {
    A_property_summary: any
    B_planning_controls: any
    C_council_pathway: any
    D_cdc_pathway: any
    E_pathway_comparison: any
    F_citations: any[]
    G_clauses_appendix?: Array<{ source: string; citation: string; source_text: string }>
    H_planning_certificate?: {
      cert_number: string | null
      council_name: string | null
      issued_at: string | null
      parsed_at: string | null
      parser_model: string | null
      confidence: string | null
      controls: Record<string, ValueT>
      eligibility_hints: {
        cdc_excluded: boolean
        cdc_excluded_reason: string | null
        cdc_excluded_source: string | null
        [k: string]: unknown
      }
      zoning: Record<string, unknown>
    } | null
    I_contract_of_sale?: {
      contract_date: string | null
      vendor: string | null
      parsed_at: string | null
      parser_model: string | null
      confidence: string | null
      encumbrances: Array<{
        type: string
        label: string
        instrument: string | null
        description: string | null
        burden: string | null
        benefit: string | null
        affected_area_m2: number | null
        fsr_limit: number | null
        height_limit_m: number | null
        max_dwellings: number | null
        source_text: string | null
      }>
      special_conditions: Array<{ summary: string; source_text: string }>
      title_details: Record<string, unknown>
      covenant_fsr_min: number | null
      covenant_height_min: number | null
      covenant_max_dwellings: number | null
      easement_total_m2: number
    } | null
    // Populated only when the requested housing form is dual occupancy
    // (attached / detached). Cross-layer LEP / DCP / SEPP feasibility
    // with conflict detection + Torrens-vs-Strata subdivision read.
    J_dual_occupancy_assessment?: {
      housing_form: string
      permitted_overall: boolean | null
      binding_min_lot_m2: number | null
      binding_min_frontage_m: number | null
      lot_meets_min: boolean | null
      frontage_meets_min: boolean | null
      torrens_subdivision_allowed: boolean | null
      subdivision_notes: string[]
      layers: Array<{
        layer: string
        permitted: boolean | null
        min_lot_m2: number | null
        min_frontage_m: number | null
        notes: string[]
        citation: string | null
        source_text?: string | null
      }>
      conflicts: string[]
      recommendations: string[]
    } | null
    // Populated when the planner picked the umbrella "Dual occupancy"
    // form — carries both sub-form assessments + the recommended one.
    // Section J renders this as a side-by-side comparison when present.
    J_dual_occupancy_comparison?: {
      attached: NonNullable<FeasibilityReport['sections']['J_dual_occupancy_assessment']>
      detached: NonNullable<FeasibilityReport['sections']['J_dual_occupancy_assessment']>
      recommended_form: 'dual_occupancy_attached' | 'dual_occupancy_detached'
      summary: string
    } | null
    // Comprehensive due-diligence checklist — every NSW residential
    // verification a planner should confirm before lodging DA / CDC.
    // Auto-ticked rows come from the pipeline; manual rows carry a
    // one-line hint pointing at where to verify. Always present.
    K_due_diligence_checklist?: DueDiligenceChecklist | null
    // Combined single-dwelling + duplex run — one block per form
    // (verdict + C / D / E + J) plus a deterministic headline. The
    // flat C / D / E / cdc_verdict above hold the PRIMARY form
    // (single dwelling). Absent / null for single-form runs.
    L_form_comparison?: FormComparison | null
  }
}

/** Per-form outlook, derived server-side from the deterministic
 *  findings (never the analyst) so the side-by-side card can't
 *  contradict the verdict it sits next to. */
export type FormOutlook = 'go' | 'caution' | 'da_only' | 'blocked'

export interface FormBlock {
  requested_form: string
  housing_form: string
  label: string        // "Single dwelling" / "Duplex"
  sublabel: string     // planning term, e.g. "dual occupancy (attached)"
  headline: {
    outlook: FormOutlook
    outlook_phrase: string
    cdc_status: 'yes' | 'conditional' | 'no' | null
    cdc_headline: string | null
    permitted: boolean | null
    council_max_gfa_m2: number | null
    cdc_max_gfa_m2: number | null
    recommended_pathway: string | null
    dual_occ_permitted: boolean | null
    dual_occ_recommended_subform: string | null
  }
  cdc_verdict: any
  C_council_pathway: any
  D_cdc_pathway: any
  E_pathway_comparison: any
  J_dual_occupancy_assessment: FeasibilityReport['sections']['J_dual_occupancy_assessment']
  J_dual_occupancy_comparison: FeasibilityReport['sections']['J_dual_occupancy_comparison']
}

export interface FormComparison {
  requested_form: string
  primary_form: string
  headline: string
  recommended_form: string | null
  recommended_label: string | null
  forms: FormBlock[]
}

export type ChecklistStatus = 'verified' | 'manual' | 'blocking' | 'not_applicable'

export interface ChecklistItem {
  id: string
  label: string
  status: ChecklistStatus
  detail: string | null
  hint: string | null
  source: string | null
}

export interface ChecklistSection {
  id: string
  title: string
  description: string
  items: ChecklistItem[]
}

export interface DueDiligenceChecklist {
  sections: ChecklistSection[]
  summary: {
    verified: number
    manual: number
    blocking: number
    not_applicable: number
  }
  total: number
}

/** Singleton consultant profile that drives the "Prepared by" block
 *  on the cover + end of every report. Fetched by the Report
 *  component on mount and cached for the session. */
export interface ConsultantProfile {
  full_name: string | null
  title: string | null
  registration_no: string | null
  email: string | null
  phone: string | null
  website: string | null
  has_photo: boolean
  photo_mime: string | null
  updated_at: string | null
}

export async function getConsultantProfile(): Promise<ConsultantProfile> {
  const res = await fetch('/api/consultant-profile')
  return jsonOrThrow(res)
}

export function consultantPhotoUrl(): string {
  return '/api/consultant-profile/photo'
}

// Admin write endpoints — `require_admin` checks ?key=<ADMIN_API_KEY>
// first (then falls back to a Supabase JWT). The Admin page passes
// its URL-bar key through; without it the backend returns 403 with
// "admin required" which is what the planner sees on the editor.
function _adminQs(adminKey: string | null | undefined): string {
  return adminKey ? `?key=${encodeURIComponent(adminKey)}` : ''
}

export async function updateConsultantProfile(
  body: {
    full_name?: string | null
    title?: string | null
    registration_no?: string | null
    email?: string | null
    phone?: string | null
    website?: string | null
  },
  adminKey?: string | null,
): Promise<ConsultantProfile> {
  const res = await fetch(`/api/admin/consultant-profile${_adminQs(adminKey)}`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  return jsonOrThrow(res)
}

export async function uploadConsultantPhoto(
  file: File,
  adminKey?: string | null,
): Promise<ConsultantProfile> {
  const form = new FormData()
  form.append('file', file)
  const res = await fetch(`/api/admin/consultant-profile/photo${_adminQs(adminKey)}`, {
    method: 'POST',
    body: form,
  })
  return jsonOrThrow(res)
}

export async function deleteConsultantPhoto(
  adminKey?: string | null,
): Promise<ConsultantProfile> {
  const res = await fetch(`/api/admin/consultant-profile/photo${_adminQs(adminKey)}`, {
    method: 'DELETE',
  })
  return jsonOrThrow(res)
}

async function jsonOrThrow(res: Response) {
  if (!res.ok) {
    // A Response body can only be consumed once. Read as text first, then
    // try JSON-decoding it for a typed `detail` field; otherwise fall back
    // to the raw text. Without this, every non-JSON 5xx (e.g. uvicorn's
    // plain-text "Internal Server Error") collapses to "HTTP 500" with no
    // actionable info in the UI.
    const text = await res.text().catch(() => '')
    let detail = text
    try {
      const parsed = JSON.parse(text)
      if (parsed && typeof parsed === 'object' && parsed.detail) {
        detail = typeof parsed.detail === 'string'
          ? parsed.detail
          : JSON.stringify(parsed.detail)
      }
    } catch { /* not JSON; keep raw text */ }
    throw new Error(detail || `HTTP ${res.status}`)
  }
  return res.json()
}

/**
 * Phase 2 async submit response. POST /api/feasibility now creates
 * a placeholder row, schedules the full pipeline as a background
 * task, and returns the report id immediately. The frontend treats
 * this as a redirect target (/r/<id>) and polls the status endpoint
 * until the pipeline finishes.
 */
export interface FeasibilitySubmitResponse {
  report_id: string
  status: string  // 'queued' | 'running' | 'ok' | 'failed'
}

/**
 * Pipeline status payload polled by the /r/<id> landing page while
 * the background job runs. Mirrors the relevant FeasibilityReport
 * output fields so we can render queued / running / ok / failed
 * states without pulling the full report payload.
 */
export interface FeasibilityPipelineStatus {
  report_id: string
  pipeline_status: 'queued' | 'running' | 'ok' | 'failed' | string
  address: string | null
  error_detail: string | null
  error_kind: string | null
  analyst_status: string | null
}

export async function getFeasibilityStatus(reportId: string): Promise<FeasibilityPipelineStatus> {
  const res = await fetch(`/api/feasibility/${reportId}/status`)
  return jsonOrThrow(res)
}

/**
 * Cancel a running feasibility. Marks the row as cancelled so the
 * background task suppresses the analyst writeback + email send at
 * its next decision point. The work already in flight isn't
 * killed — but the planner gets what they asked for (no email,
 * no completion).
 */
export async function cancelFeasibility(reportId: string): Promise<FeasibilityPipelineStatus> {
  const res = await fetch(`/api/feasibility/${reportId}/cancel`, { method: 'POST' })
  return jsonOrThrow(res)
}

export async function runFeasibility(req: FeasibilityRequest): Promise<FeasibilitySubmitResponse> {
  // One retry on network-class failure. Safari surfaces a flaky
  // connection (proxy dropped, brief TLS hiccup, server warm-up) as
  // "TypeError: Load failed" — the request never actually reached
  // the server, so retrying is safe. HTTP errors (jsonOrThrow throws
  // for !res.ok) bubble out unchanged because those mean the server
  // did respond and a retry would just hit the same response.
  const post = () =>
    fetch('/api/feasibility', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(req),
    })
  let res: Response
  try {
    res = await post()
  } catch {
    await new Promise((r) => setTimeout(r, 2000))
    res = await post()
  }
  return jsonOrThrow(res)
}

export interface AnalystStatusResponse {
  analyst: AnalystOutput | null
  analyst_status: string | null
  analyst_failure_reason: string | null
}

export async function getAnalystStatus(reportId: string): Promise<AnalystStatusResponse> {
  const res = await fetch(`/api/feasibility/${reportId}/analyst`)
  return jsonOrThrow(res)
}

/**
 * Load a previously-saved feasibility report by id. Powers the
 * email-link landing page (/r/<id>) so the planner can come back
 * to a finished report after the analyst background task lands.
 * Throws on 404 / auth failure.
 */
/**
 * Ask-the-report helper. One free-form question at a time about a
 * finished report; the backend grounds the answer in the saved
 * report payload and the vendored Codes SEPP / Housing SEPP text,
 * and keeps the thread on the report row.
 */
export interface AskExchange {
  id: string
  question: string
  answer: string
  asked_at: string
  model?: string | null
  asked_by?: string | null
}

export interface AskHistory {
  report_id: string
  available: boolean
  suggested_questions: string[]
  history: AskExchange[]
}

export interface AskResponse extends AskHistory {
  exchange: AskExchange
}

export async function getAskHistory(reportId: string): Promise<AskHistory> {
  const res = await fetch(`/api/feasibility/${reportId}/ask`)
  return jsonOrThrow(res)
}

export async function askReport(reportId: string, question: string): Promise<AskResponse> {
  const res = await fetch(`/api/feasibility/${reportId}/ask`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ question }),
  })
  return jsonOrThrow(res)
}

export async function getFeasibilityReport(reportId: string): Promise<FeasibilityReport> {
  const res = await fetch(`/api/feasibility/${reportId}`)
  return jsonOrThrow(res)
}

export async function submitOverride(body: {
  target_type: 'lep' | 'dcp'
  lga_slug: string
  zone?: string | null
  housing_form?: string | null
  field_key: string
  proposed_value: unknown
  source_clause?: string | null
  source_text?: string | null
  submitter_email?: string | null
  submitter_note?: string | null
}): Promise<{ id: number; status: string }> {
  const res = await fetch('/api/overrides', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  return jsonOrThrow(res)
}

export function pdfUrl(reportId: string): string {
  return `/api/exports/${reportId}.pdf`
}

export type DocKind = 'cert' | 'contract'

export interface PendingDoc {
  jobId: string
  kind: DocKind
  fileName: string
}

export interface UploadJobStatus {
  job_id: string
  kind: string
  status: 'pending' | 'running' | 'succeeded' | 'failed'
  error: string | null
  result: {
    findings_count?: number
    encumbrances_count?: number
    // Set when an embedded Section 10.7 was detected + parsed out of
    // a contract-of-sale PDF (the s10.7 is almost always bound into
    // the contract pack around pages 30-50). Number is the count of
    // pages extracted; null/undefined means no embedded cert was
    // detected on this upload.
    embedded_cert_pages?: number | null
    [k: string]: unknown
  } | null
  started_at?: string | null
  finished_at?: string | null
}

export async function getUploadJob(jobId: string): Promise<UploadJobStatus> {
  const res = await fetch(`/api/uploads/jobs/${jobId}`)
  return jsonOrThrow(res)
}

export interface RecaptureSourceResult {
  source_text: string | null
  reason: string | null
}

export async function recaptureSource(body: {
  source_type: 'lep' | 'dcp'
  lga_slug: string
  citation: string
  field_key?: string | null
}): Promise<RecaptureSourceResult> {
  const res = await fetch('/api/recapture-source', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  return jsonOrThrow(res)
}
